import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import CollectionPreview from './components/CollectionPreview';
import TrustSignals from './components/TrustSignals';
import SuccessStories from './components/SuccessStories';
import PlatformFeatures from './components/PlatformFeatures';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';

const Homepage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/collection-dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Smooth scroll behavior for anchor links
    const handleSmoothScroll = (e) => {
      const target = e?.target?.closest('a[href^="#"]');
      if (target) {
        e?.preventDefault();
        const targetId = target?.getAttribute('href')?.substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>Crazy Rare Club - Premium Collectibles Portfolio Management</title>
        <meta 
          name="description" 
          content="Transform your collecting passion into sophisticated portfolio management. Track, analyze, and grow your collectibles with professional-grade tools and real-time market intelligence." 
        />
        <meta 
          name="keywords" 
          content="collectibles, portfolio management, market analysis, authentication, investment tracking, rare items, vintage collection" 
        />
        <meta property="og:title" content="Crazy Rare Club - Where Collecting Meets Investing" />
        <meta 
          property="og:description" 
          content="Join 15,000+ collectors using professional-grade tools to manage $2.4M+ in portfolio value. Your passion, precisely tracked." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://crazyrareclub.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Crazy Rare Club - Premium Collectibles Platform" />
        <meta 
          name="twitter:description" 
          content="Professional portfolio management for serious collectors. Real-time market intelligence, authentication network, and advanced analytics." 
        />
        <link rel="canonical" href="https://crazyrareclub.com" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header Navigation */}
        <Header />

        {/* Main Content */}
        <main className="pt-16">
          {/* Hero Section */}
          <HeroSection />

          {/* Collection Preview Carousel */}
          <CollectionPreview />

          {/* Trust Signals & Social Proof */}
          <TrustSignals />

          {/* Success Stories */}
          <SuccessStories />

          {/* Platform Features */}
          <PlatformFeatures />

          {/* Call to Action */}
          <CallToAction />
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Homepage;